import React from 'react';

/**
 * AnalyticsCharts - Component for displaying analytics visualizations
 * Shows topic trends, engagement scores, and activity patterns
 */

// Simple bar chart component
const BarChart = ({ data, maxValue, label }) => {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);

  return (
    <div className="space-y-3">
      {data.map((item, index) => {
        const percentage = (item.value / max) * 100;
        return (
          <div key={index} className="flex items-center gap-3">
            <div className="w-24 text-sm text-gray-700 font-medium truncate" title={item.label}>
              {item.label}
            </div>
            <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
              <div
                className="bg-indigo-600 h-6 rounded-full transition-all duration-500 flex items-center justify-end px-2"
                style={{ width: `${Math.max(percentage, 5)}%` }}
              >
                <span className="text-xs text-white font-semibold">
                  {item.value}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Simple line chart component (using text-based visualization)
const LineChart = ({ data, label }) => {
  if (!data || data.length === 0) return null;

  const values = data.map(d => d.value);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between h-32 border-b border-l border-gray-300 pl-2 pb-2 gap-1">
        {data.map((item, index) => {
          const height = ((item.value - min) / range) * 100;
          return (
            <div key={index} className="flex-1 flex flex-col justify-end items-center group">
              <div className="relative w-full">
                <div
                  className="bg-indigo-500 w-full rounded-t transition-all duration-300 hover:bg-indigo-600"
                  style={{ height: `${Math.max(height, 5)}px` }}
                  title={`${item.label}: ${item.value}`}
                >
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span>{data[0]?.label}</span>
        <span>{data[Math.floor(data.length / 2)]?.label}</span>
        <span>{data[data.length - 1]?.label}</span>
      </div>
    </div>
  );
};

// Engagement heatmap component
const EngagementHeatmap = ({ students }) => {
  const getEngagementColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getEngagementLabel = (score) => {
    if (score >= 80) return 'High';
    if (score >= 60) return 'Medium';
    if (score >= 40) return 'Low';
    return 'Very Low';
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
      {students.slice(0, 20).map((student) => (
        <div
          key={student.student_id}
          className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-3 h-3 rounded-full ${getEngagementColor(student.engagement_score)}`}></div>
            <span className="text-xs font-semibold text-gray-700 truncate" title={student.username}>
              {student.first_name || student.username}
            </span>
          </div>
          <div className="text-lg font-bold text-gray-900">
            {student.engagement_score.toFixed(0)}%
          </div>
          <div className="text-xs text-gray-500">
            {getEngagementLabel(student.engagement_score)}
          </div>
        </div>
      ))}
    </div>
  );
};

// Topic trends visualization
export const TopicTrendsChart = ({ topics }) => {
  if (!topics || topics.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No topic data available yet
      </div>
    );
  }

  const chartData = topics.map(t => ({
    label: t.topic,
    value: t.count
  }));

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        📚 Most Discussed Topics
      </h3>
      <BarChart data={chartData} label="Topic" />
    </div>
  );
};

// Engagement overview chart
export const EngagementOverviewChart = ({ students }) => {
  if (!students || students.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No student data available yet
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          🎯 Student Engagement Heatmap
        </h3>
        <div className="flex gap-2 text-xs">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            High (80%+)
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            Medium (60-79%)
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            Low (&lt;60%)
          </span>
        </div>
      </div>
      <EngagementHeatmap students={students} />
    </div>
  );
};

// Session activity timeline
export const SessionActivityChart = ({ sessionData }) => {
  if (!sessionData || sessionData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No session data available yet
      </div>
    );
  }

  const chartData = sessionData.map(d => ({
    label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: d.count
  }));

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        📈 Session Activity (Last 30 Days)
      </h3>
      <LineChart data={chartData} label="Date" />
    </div>
  );
};

// Struggling students alert widget
export const StrugglingStudentsWidget = ({ strugglingStudents }) => {
  if (!strugglingStudents || strugglingStudents.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <span className="text-3xl">✅</span>
          <div>
            <h3 className="font-semibold text-green-800">All Students On Track</h3>
            <p className="text-sm text-green-700">
              No students showing signs of struggle. Great work!
            </p>
          </div>
        </div>
      </div>
    );
  }

  const highSeverity = strugglingStudents.filter(s => s.severity === 'high');
  const mediumSeverity = strugglingStudents.filter(s => s.severity === 'medium');

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
      <div className="flex items-start gap-3 mb-4">
        <span className="text-3xl">⚠️</span>
        <div>
          <h3 className="font-semibold text-yellow-900">Students Needing Attention</h3>
          <p className="text-sm text-yellow-700">
            {strugglingStudents.length} student{strugglingStudents.length !== 1 ? 's' : ''} showing signs of struggle
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {highSeverity.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-700 mb-2">🔴 HIGH PRIORITY ({highSeverity.length})</p>
            <div className="space-y-1">
              {highSeverity.map((student) => (
                <div key={student.student_id} className="text-sm text-red-800">
                  <span className="font-medium">{student.username}</span>
                  <span className="text-red-600"> - {student.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {mediumSeverity.length > 0 && (
          <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3">
            <p className="text-xs font-semibold text-yellow-800 mb-2">🟡 MEDIUM PRIORITY ({mediumSeverity.length})</p>
            <div className="space-y-1">
              {mediumSeverity.slice(0, 5).map((student) => (
                <div key={student.student_id} className="text-sm text-yellow-900">
                  <span className="font-medium">{student.username}</span>
                  <span className="text-yellow-700"> - {student.reason}</span>
                </div>
              ))}
              {mediumSeverity.length > 5 && (
                <p className="text-xs text-yellow-600 mt-1">
                  ... and {mediumSeverity.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// AI Insights Summary Widget
export const AIInsightsSummaryWidget = ({ insightsSummary }) => {
  if (!insightsSummary) return null;

  const { total_insights, new_insights, insights_by_type, common_struggles } = insightsSummary;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">🤖</span>
        <div>
          <h3 className="font-semibold text-gray-900">AI Insights Summary</h3>
          <p className="text-sm text-gray-600">
            {total_insights} total insights, {new_insights} need review
          </p>
        </div>
      </div>

      {new_insights > 0 && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <p className="text-sm text-indigo-800 font-medium">
            📬 {new_insights} new insight{new_insights !== 1 ? 's' : ''} awaiting your review
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center p-3 bg-red-50 rounded-lg">
          <div className="text-2xl font-bold text-red-700">{insights_by_type?.struggle_area || 0}</div>
          <div className="text-xs text-red-600">Struggle Areas</div>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-700">{insights_by_type?.strength_area || 0}</div>
          <div className="text-xs text-green-600">Strengths</div>
        </div>
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-700">{insights_by_type?.topic_trend || 0}</div>
          <div className="text-xs text-blue-600">Topic Trends</div>
        </div>
        <div className="text-center p-3 bg-yellow-50 rounded-lg">
          <div className="text-2xl font-bold text-yellow-700">{insights_by_type?.engagement_alert || 0}</div>
          <div className="text-xs text-yellow-600">Alerts</div>
        </div>
      </div>

      {common_struggles && common_struggles.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Common Class Struggles:</p>
          <div className="space-y-1">
            {common_struggles.slice(0, 3).map((struggle, index) => (
              <div key={index} className="text-sm text-gray-600 flex justify-between">
                <span>{struggle.topic}</span>
                <span className="text-gray-500">{struggle.student_count} student{struggle.student_count !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AnalyticsCharts = {
  TopicTrendsChart,
  EngagementOverviewChart,
  SessionActivityChart,
  StrugglingStudentsWidget,
  AIInsightsSummaryWidget
};

export default AnalyticsCharts;
